import { faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import React, { useState } from "react";

import "./PremiumFeatureGate.css";
import UpgradeModal from "./UpgradeModal";
import { usePremiumFeature } from "../../../hooks";

const PremiumFeatureGate = ({
  children,
  featureName,
  iconPosition = "center",
  iconSize = "small",
  requiredPermission,
  className = "",
}) => {
  const { hasFeatureAccess } = usePremiumFeature();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { freeMode } = useFlags();

  if (!freeMode) {
    return children;
  }

  // If user has required permission and we're not forcing the gate, render children as-is
  if (hasFeatureAccess(requiredPermission)) {
    return children;
  }

  const handleOpenModal = () => {
    setShowUpgradeModal(true);
    mixpanel.track("Clicked Premium Feature Gate", {
      featureName,
      requiredPermission,
    });
  };

  // Get the icon size dimensions
  const getIconSizeStyles = () => {
    switch (iconSize) {
      case "xs":
        return {
          width: "18px",
          height: "18px",
          padding: "3px",
          fontSize: "0.7rem",
        };
      case "small":
        return {
          width: "24px",
          height: "24px",
          padding: "4px",
          fontSize: "0.85rem",
        };
      case "large":
        return {
          width: "40px",
          height: "40px",
          padding: "7px",
          fontSize: "1.35rem",
        };
      case "medium":
      default:
        return {
          width: "32px",
          height: "32px",
          padding: "5px",
          fontSize: "1.1rem",
        };
    }
  };

  // Get the icon position styles
  const getIconPositionStyles = () => {
    switch (iconPosition) {
      case "middle-right":
        return {
          position: "absolute",
          top: "50%",
          right: "8px",
          transform: "translateY(-50%)",
          zIndex: 30,
          pointerEvents: "none",
        };
      case "center":
        return {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
      case "top-right":
      default:
        return {
          position: "absolute",
          top: "-8px",
          right: "-8px",
        };
    }
  };

  const sizeStyles = getIconSizeStyles();
  const positionStyles = getIconPositionStyles();

  return (
    <>
      <UpgradeModal
        shouldShow={showUpgradeModal}
        setShouldShow={setShowUpgradeModal}
        featureName={featureName}
      />
      <div
        className={`premium-feature-gate relative cursor-pointer ${className}`}
        onClick={handleOpenModal}
        data-testid={`premium-feature-gate-${featureName}`}
      >
        <div className="premium-feature-gate-content">{children}</div>
        <div
          className="premium-feature-gate-overlay"
          style={{
            backgroundColor: "transparent",
          }}
        >
          <div
            className="lock-icon"
            style={{
              ...sizeStyles,
              ...positionStyles,
              backgroundColor: "rgba(79, 70, 229, 0.95)",
              borderRadius: "9999px",
              padding: "0.375rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 20,
            }}
          >
            <FontAwesomeIcon
              icon={faLock}
              size={iconSize}
              className="text-yellow-300"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PremiumFeatureGate;
